import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls';

import { ElevatorManager } from '../Elevator/ElevatorManager';
import { ArtworkManager } from '../Museum/ArtworkManager';
import { MuseumStructure } from '../Museum/MuseumStructure';
import { AudioManager } from '../Museum/AudioManager';

const BOUNDS = { minX: -12.5, maxX: 12.5, minZ: -12.5, maxZ: 12.5 };
const GROUND_CAM_Y = 1.7;
const UPPER_CAM_Y = 6.2 + 1.7;

function App() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const textureLoaderRef = useRef(null);

  const [isLocked, setIsLocked] = useState(false);
  const [isMobile] = useState(() => 'ontouchstart' in window || navigator.maxTouchPoints > 0);
  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [elevatorLabel, setElevatorLabel] = useState('');

  const moveForward = useRef(false);
  const moveBackward = useRef(false);
  const moveLeft = useRef(false);
  const moveRight = useRef(false);
  const velocity = useRef(new THREE.Vector3());
  const direction = useRef(new THREE.Vector3());
  const prevTime = useRef(0);

  const elevatorManager = useRef(null);
  const artworkManager = useRef(null);
  const museumStructure = useRef(null);
  const audioManager = useRef(null);
  const animationFrameId = useRef(null);
  const lastElevLabel = useRef('');

  const touchActive = useRef(false);
  const touchPrev = useRef(null);
  const eulerRef = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));

  useEffect(() => {
    if (!containerRef.current) return;

    const loadingManager = new THREE.LoadingManager();
    loadingManager.onProgress = (url, loaded, total) => {
      setLoadProgress(Math.round((loaded / total) * 100));
    };
    loadingManager.onLoad = () => {
      setTimeout(() => {
        setIsLoading(false);
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          setIsLocked(true);
        }
      }, 500);
    };
    loadingManager.onError = (url) => { console.warn('Failed to load:', url); };

    cameraRef.current = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    cameraRef.current.position.set(0, 1.7, 35);
    cameraRef.current.lookAt(0, 3, 0);

    rendererRef.current = new THREE.WebGLRenderer({ antialias: !isMobile, alpha: false });
    rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    rendererRef.current.shadowMap.enabled = !isMobile;
    if (!isMobile) rendererRef.current.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(rendererRef.current.domElement);

    if (!isMobile) {
      controlsRef.current = new PointerLockControls(cameraRef.current, document.body);
      controlsRef.current.addEventListener('lock', () => setIsLocked(true));
      controlsRef.current.addEventListener('unlock', () => setIsLocked(false));
    }

    textureLoaderRef.current = new THREE.TextureLoader(loadingManager);
    sceneRef.current = new THREE.Scene();

    museumStructure.current = new MuseumStructure(sceneRef.current, textureLoaderRef.current);
    museumStructure.current.buildAll();
    artworkManager.current = new ArtworkManager(sceneRef.current, textureLoaderRef.current);
    artworkManager.current.loadArtworks();
    elevatorManager.current = new ElevatorManager(sceneRef.current, cameraRef.current, textureLoaderRef.current);

    audioManager.current = new AudioManager(cameraRef.current);
    audioManager.current.addZone('elevator', { minX: -2, maxX: 2, minY: 0, maxY: 12, minZ: -6, maxZ: 0 }, '/assets/Audio/elevator.mp3', 0.3);
    audioManager.current.addZone('ground-floor', { minX: -13, maxX: 13, minY: 0, maxY: 6, minZ: -13, maxZ: 13 }, '/assets/Audio/ground-floor.mp3', 0.4);
    audioManager.current.addZone('second-floor', { minX: -13, maxX: 13, minY: 6, maxY: 12, minZ: -13, maxZ: 13 }, '/assets/Audio/second-floor.mp3', 0.4);

    const onKeyDown = (e) => {
      switch (e.code) {
        case 'ArrowUp': case 'KeyW': moveForward.current = true; break;
        case 'ArrowDown': case 'KeyS': moveBackward.current = true; break;
        case 'ArrowLeft': case 'KeyA': moveLeft.current = true; break;
        case 'ArrowRight': case 'KeyD': moveRight.current = true; break;
        default: break;
      }
    };
    const onKeyUp = (e) => {
      switch (e.code) {
        case 'ArrowUp': case 'KeyW': moveForward.current = false; break;
        case 'ArrowDown': case 'KeyS': moveBackward.current = false; break;
        case 'ArrowLeft': case 'KeyA': moveLeft.current = false; break;
        case 'ArrowRight': case 'KeyD': moveRight.current = false; break;
        default: break;
      }
    };
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };

    const onTouchStart = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      touchActive.current = true;
      touchPrev.current = { x: t.clientX, y: t.clientY };
      try { audioManager.current?.start(); } catch (err) { /* ignore */ }
    };
    const onTouchMove = (e) => {
      e.preventDefault();
      if (!cameraRef.current || !touchPrev.current) return;
      const t = e.touches[0];
      const dx = t.clientX - touchPrev.current.x;
      const dy = t.clientY - touchPrev.current.y;
      eulerRef.current.setFromQuaternion(cameraRef.current.quaternion);
      eulerRef.current.y -= dx * 0.005;
      eulerRef.current.x -= dy * 0.003;
      eulerRef.current.x = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, eulerRef.current.x));
      cameraRef.current.quaternion.setFromEuler(eulerRef.current);
      touchPrev.current = { x: t.clientX, y: t.clientY };
    };
    const onTouchEnd = (e) => {
      e.preventDefault();
      if (e.touches.length === 0) {
        touchActive.current = false;
        touchPrev.current = null;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('resize', handleResize);

    if (isMobile) {
      document.addEventListener('touchstart', onTouchStart, { passive: false });
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd, { passive: false });
      document.addEventListener('touchcancel', onTouchEnd, { passive: false });
    } else {
      containerRef.current.addEventListener('click', () => controlsRef.current?.lock());
    }

    prevTime.current = performance.now();

    const animate = () => {
      animationFrameId.current = requestAnimationFrame(animate);
      if (document.hidden) return;
      const now = performance.now();
      const delta = Math.min((now - prevTime.current) / 1000, 0.1);
      prevTime.current = now;

      if (!isMobile && controlsRef.current) {
        velocity.current.x -= velocity.current.x * 3.0 * delta;
        velocity.current.z -= velocity.current.z * 3.0 * delta;
        direction.current.z = Number(moveForward.current) - Number(moveBackward.current);
        direction.current.x = Number(moveRight.current) - Number(moveLeft.current);
        direction.current.normalize();
        if (moveForward.current || moveBackward.current) velocity.current.z -= direction.current.z * 120.0 * delta;
        if (moveLeft.current || moveRight.current) velocity.current.x -= direction.current.x * 120.0 * delta;
        const mv = 3.0;
        velocity.current.x = Math.max(Math.min(velocity.current.x, mv), -mv);
        velocity.current.z = Math.max(Math.min(velocity.current.z, mv), -mv);
        controlsRef.current.moveRight(-velocity.current.x * delta);
        controlsRef.current.moveForward(-velocity.current.z * delta);
      }

      // Mobile touch walk — slower speed so users can look around
      if (isMobile && touchActive.current && cameraRef.current) {
        const walkDir = new THREE.Vector3();
        cameraRef.current.getWorldDirection(walkDir);
        walkDir.y = 0;
        walkDir.normalize();
        cameraRef.current.position.addScaledVector(walkDir, 1.8 * delta);
      }

      // SIMPLIFIED COLLISION: gallery (open front) + outdoor
      if (cameraRef.current) {
        const cam = cameraRef.current.position;
        const isInGallery = cam.x > BOUNDS.minX && cam.x < BOUNDS.maxX &&
                            cam.z > BOUNDS.minZ && cam.z < BOUNDS.maxZ + 5;

        // Toggle exterior shell visibility:
        // Hide when inside the gallery so the sky dome renders clean.
        // Show when outside so the Guggenheim building looks solid.
        if (museumStructure.current?.exteriorShell) {
          museumStructure.current.exteriorShell.visible = !isInGallery;
        }

        if (isInGallery) {
          cam.x = Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, cam.x));
          cam.z = Math.max(BOUNDS.minZ, cam.z);
          const elevState = elevatorManager.current?.state;
          if (elevState !== 4) { cam.y = cam.y > 4 ? UPPER_CAM_Y : GROUND_CAM_Y; }
        } else {
          cam.y = GROUND_CAM_Y;
          const distFromCenter = Math.sqrt(cam.x * cam.x + cam.z * cam.z);
          // Building wall collision (except entrance corridor)
          if (distFromCenter < 17.5 && !(Math.abs(cam.x) < 5 && cam.z > 0)) {
            const angle = Math.atan2(cam.z, cam.x);
            cam.x = Math.cos(angle) * 17.5;
            cam.z = Math.sin(angle) * 17.5;
          }
          if (distFromCenter > 60) {
            const angle = Math.atan2(cam.z, cam.x);
            cam.x = Math.cos(angle) * 60;
            cam.z = Math.sin(angle) * 60;
          }
        }
      }

      elevatorManager.current?.update();
      const elLabel = elevatorManager.current?.floorLabel || '';
      if (elLabel !== lastElevLabel.current) { lastElevLabel.current = elLabel; setElevatorLabel(elLabel); }
      if (museumStructure.current?.skyDome) { museumStructure.current.skyDome.rotation.y += 0.0001; }
      museumStructure.current?.updateDust(now * 0.001);

      const textures = artworkManager.current?.animatedTextures;
      if (textures) {
        for (let i = 0; i < textures.length; i++) {
          const a = textures[i];
          if (!a || !a.frames) continue;
          if (now - a.lastTime >= a.delays[a.currentFrame]) {
            a.currentFrame = (a.currentFrame + 1) % a.frames.length;
            a.ctx.putImageData(a.frames[a.currentFrame], 0, 0);
            a.texture.needsUpdate = true;
            a.lastTime = now;
          }
        }
      }

      audioManager.current?.update();
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    const container = containerRef.current;
    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('resize', handleResize);
      if (isMobile) {
        document.removeEventListener('touchstart', onTouchStart);
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
        document.removeEventListener('touchcancel', onTouchEnd);
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (container && rendererRef.current.domElement.parentNode === container) {
          container.removeChild(rendererRef.current.domElement);
        }
      }
      audioManager.current?.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnter = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    try { audioManager.current?.start(); } catch (err) { /* */ }
    if (isMobile) {
      setIsLocked(true);
    } else {
      controlsRef.current?.lock();
    }
  };

  return (
    <div ref={containerRef} style={{
      width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0,
      touchAction: 'none', overflow: 'hidden',
      WebkitTouchCallout: 'none', WebkitUserSelect: 'none',
    }}>
      {isLoading && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: '#0a0a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', zIndex: 20, fontFamily: 'Georgia, serif' }}>
          <div style={{ fontSize: '24px', marginBottom: '30px', fontWeight: 'bold' }}>The School of Nelsonville</div>
          <div style={{ width: '200px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ width: `${loadProgress}%`, height: '100%', background: 'white', borderRadius: '2px', transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ fontSize: '12px', marginTop: '12px', opacity: 0.4 }}>Loading... {loadProgress}%</div>
        </div>
      )}

      {!isMobile && isLocked && <div className="dot" />}

      {isLocked && !isLoading && (
        <button onClick={(e) => { e.stopPropagation(); try { const m = audioManager.current?.toggleMute(); setIsMuted(m); } catch(err) {} }}
          style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 15, width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'manipulation' }}
          title={isMuted ? 'Unmute' : 'Mute'}>
          {isMuted ? '\u{1F507}' : '\u{1F50A}'}
        </button>
      )}

      {isLocked && !isLoading && elevatorLabel && (
        <div style={{ position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 15, pointerEvents: 'none', textAlign: 'center' }}>
          <div style={{ background: 'rgba(0,0,0,0.6)', borderRadius: '12px', padding: '10px 24px', fontFamily: 'Georgia, serif', color: '#c4a87c' }}>
            <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
              {elevatorLabel === '\u25b2' ? 'Going up...' : elevatorLabel === '\u25bc' ? 'Going down...' : `Floor ${elevatorLabel}`}
            </div>
          </div>
        </div>
      )}

      {!isMobile && !isLoading && !isLocked && (
        <div onClick={handleEnter} style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: 'white', textAlign: 'center', userSelect: 'none', cursor: 'pointer', zIndex: 10,
        }}>
          <div style={{ fontSize: '28px', marginBottom: '12px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
            The School of Nelsonville Museum
          </div>
          <div style={{ fontSize: '20px', marginBottom: '8px', opacity: 0.8, fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
            Happy Birthday Bill!
          </div>
          <div style={{ fontSize: '20px', marginBottom: '20px', opacity: 0.9 }}>Click to Enter</div>
          <small style={{ fontSize: '13px', opacity: 0.6, lineHeight: 1.6 }}>
            WASD to move {'\u2022'} Mouse to look {'\u2022'} ESC to release
          </small>
        </div>
      )}

      {isMobile && isLocked && !isLoading && (
        <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, pointerEvents: 'none', textAlign: 'center' }}>
          <div style={{ background: 'rgba(0,0,0,0.5)', borderRadius: '8px', padding: '8px 16px', color: 'white', fontSize: '12px', opacity: 0.7, fontFamily: 'Georgia, serif' }}>
            Touch to walk {'\u2022'} Drag to look
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
